/**
 * Shipping info section on the product detail page.
 *
 * One-line summary ("Shipping to {city}: default ETA + lowest price") that
 * opens a modal showing the full matrix. Customer location is captured via
 * Google Places (or current location) so distance-based pricing is accurate.
 */
import React, { useMemo, useState } from 'react';
import { FiTruck, FiX } from 'react-icons/fi';
import AnimatedModal from '../../Components/AnimatedModal';
import DeliveryAddressPicker from '../../Components/DeliveryAddressPicker';
import { deliveryLocationLabel, saveDeliveryLocation } from '../../utils/deliveryLocation';
import useShippingOptions, { announceDeliveryLocation, initialDeliveryLocation } from '../../hooks/useShippingOptions';
import { translateShippingEta, translateShippingLabel } from '../../utils/shippingCopy';
import { currencySymbol, shopAmount, shopPrice } from '../../utils/currency';
import { useCurrency } from '../../Context/CurrencyContext';
import styles from './shippingInfoSection.module.css';

const ShippingInfoSection = ({ salonSlug, variant, cartTotal = 0, t }) => {
  const currency = useCurrency();
  const [location, setLocation] = useState(initialDeliveryLocation);
  // Shared with the line under the price (DeliveryPromiseLine): one request between them.
  const resolved = useShippingOptions(salonSlug, variant?.id, location);
  const [open, setOpen] = useState(false);

  const locationLabel = deliveryLocationLabel(location);

  const persistLocation = (next) => {
    setLocation(next);
    saveDeliveryLocation(next);
    announceDeliveryLocation(next);
  };

  const deliveryOptions = (resolved?.options ?? []).filter((o) => o.method === 'DELIVERY');
  const pickupOption    = (resolved?.options ?? []).find((o) => o.method === 'PICKUP');

  const cheapestDelivery = useMemo(
    () => deliveryOptions.sort((a, b) => a.price - b.price)[0],
    [deliveryOptions],
  );

  // Find the lowest free-shipping threshold across delivery tiers, so we can
  // surface "Add X more for free delivery" / "you qualify" banners.
  const freeDeliveryInfo = useMemo(() => {
    const hasFreeAlready = deliveryOptions.some((o) => o.price === 0 && o.freeAbove != null);
    const candidates = deliveryOptions
      .filter((o) => o.freeAbove != null)
      .map((o) => Number(o.freeAbove))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!candidates.length) return null;
    const threshold = Math.min(...candidates);
    const gap = Math.max(0, threshold - Number(cartTotal || 0));
    return { threshold, gap, qualified: hasFreeAlready || gap <= 0 };
  }, [deliveryOptions, cartTotal]);
  const summaryRight = !resolved
    ? t('common.loading', { defaultValue: 'Loading…' })
    : cheapestDelivery
      ? (cheapestDelivery.price === 0
          ? `${t('shipping.free', { defaultValue: 'Free' })} · ${translateShippingEta(cheapestDelivery.etaCopy, t)}`
          : `${shopPrice(cheapestDelivery.price, currency)} · ${translateShippingEta(cheapestDelivery.etaCopy, t)}`)
      : (pickupOption
          ? `${t('shipping.pickupOnly', { defaultValue: 'Pickup only' })}`
          : t('shipping.notAvailable', { defaultValue: 'Not available' }));

  return (
    <div className={styles.section} data-testid="product-detail-shipping-info">
      <button
        type="button"
        className={styles.row}
        onClick={() => setOpen(true)}
        data-testid="product-detail-shipping-info-toggle"
      >
        <FiTruck className={styles.icon} aria-hidden />
        <span className={styles.left}>
          <span className={styles.title}>
            {locationLabel
              ? t('shipping.shipTo', { defaultValue: 'Ship to {{city}}', city: locationLabel })
              : t('shipping.shipToPrompt', { defaultValue: 'Set delivery location' })}
          </span>
          <span className={styles.subtitle}>
            {t('shipping.tapToSeeAll', { defaultValue: 'Tap to see all delivery types' })}
          </span>
        </span>
        <span className={styles.right}>{summaryRight}</span>
      </button>

      <AnimatedModal
        open={open}
        onClose={() => setOpen(false)}
        sheetClassName={styles.sheet}
        testId="shipping-matrix-modal"
        ariaLabel={t('shipping.matrixTitle', { defaultValue: 'Shipping options' })}
      >
        {({ requestClose }) => (
          <>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={requestClose}
              aria-label={t('common.close', { defaultValue: 'Close' })}
              data-testid="shipping-matrix-modal-close"
            >
              <FiX />
            </button>
            <h2 className={styles.modalTitle}>
              {t('shipping.matrixTitle', { defaultValue: 'Shipping options' })}
            </h2>
            <div className={styles.locationRow}>
              <DeliveryAddressPicker
                value={location || { address: '', city: '', country: '', lat: null, lng: null }}
                onChange={persistLocation}
                label={t('shipping.locationLabel', { defaultValue: 'Delivery location' })}
                placeholder={t('shipping.locationPlaceholder', { defaultValue: 'Search address or city…' })}
                testIdPrefix="shipping-location"
                t={t}
              />
            </div>

            {freeDeliveryInfo && (
              <div
                className={`${styles.freeBanner} ${freeDeliveryInfo.qualified ? styles.freeBannerQualified : ''}`}
                data-testid="shipping-free-banner"
              >
                {freeDeliveryInfo.qualified
                  ? t('shipping.freeUnlocked', {
                      defaultValue: '🎉 Your order qualifies for free delivery!',
                    })
                  : t('shipping.freeGap', {
                      defaultValue: 'Add {{currency}}{{n}} more to unlock free delivery',
                      n: shopAmount(freeDeliveryInfo.gap, currency),
                      currency: currencySymbol(currency),
                    })}
              </div>
            )}

            <div className={styles.matrix}>
              <div className={styles.matrixHeader}>
                <span>{t('shipping.matrixType', { defaultValue: 'Type' })}</span>
                <span>{t('shipping.matrixEta',  { defaultValue: 'Est. delivery' })}</span>
                <span>{t('shipping.matrixPrice', { defaultValue: 'Price' })}</span>
              </div>
              {pickupOption && (
                <div className={styles.matrixRow}>
                  <span className={styles.cellType}>{t('shipping.pickup', { defaultValue: 'Pickup from shop' })}</span>
                  <span className={styles.cellEta}>{translateShippingEta(pickupOption.etaCopy, t)}</span>
                  <span className={styles.cellPrice}>{t('shipping.free', { defaultValue: 'Free' })}</span>
                </div>
              )}
              {deliveryOptions.length === 0 && !pickupOption && (
                <div className={styles.emptyRow}>
                  {t('shipping.matrixEmpty', { defaultValue: 'No shipping configured for this product yet.' })}
                </div>
              )}
              {deliveryOptions.map((opt) => (
                <div key={opt.tierId} className={styles.matrixRow} data-testid={`shipping-matrix-row-${opt.durationBucket}`}>
                  <span className={styles.cellType}>{translateShippingLabel(opt.label, t)}</span>
                  <span className={styles.cellEta}>{translateShippingEta(opt.etaCopy, t)}</span>
                  <span className={styles.cellPrice}>
                    {opt.price === 0 ? t('shipping.free', { defaultValue: 'Free' }) : `${shopPrice(opt.price, currency)}`}
                    {opt.freeAbove != null && opt.price > 0 && (
                      <span className={styles.cellFreeAboveHint}>
                        {t('shipping.freeAbove', { defaultValue: 'Free over {{currency}}{{n}}', n: opt.freeAbove, currency: currencySymbol(currency) })}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>

            <p className={styles.policyNote}>
              {t('shipping.policyNote', {
                defaultValue: 'Final shipping fees are calculated once the order is placed.',
              })}
            </p>
          </>
        )}
      </AnimatedModal>
    </div>
  );
};

export default ShippingInfoSection;
